import { percentChange, sumRounded } from '../../Utils/decimalUtils';
import type { components } from '../../Types/api';
import type { AdjustmentValue, TierAdjustmentValue } from './PriceSimulation.types';

type Schemas = components['schemas'];
export type PackageSimulationDto = Schemas['Cockpit.Models.PriceSimulation.PackageSimulationDto'];
export type TierDto = Schemas['Cockpit.Models.PriceSimulation.TierDto'];
export type ProductGroupDto = Schemas['Cockpit.Models.PriceSimulation.ProductGroupDto'];
export type ProductDto = Schemas['Cockpit.Models.PriceSimulation.ProductDto'];
export type PricePointDto = Schemas['Cockpit.Models.PriceSimulation.PriceSimulationPointDto'];
export type TieredPricePointDto = Schemas['Cockpit.Models.PriceSimulation.TieredPricePointDto'];
export type PricingSchemeType = Schemas['Cockpit.Models.PriceSimulation.PricingSchemeType'];
export type ItemApprovalStatus = Schemas['Cockpit.Models.PriceSimulation.ItemApprovalStatus'];

export const PRICING_SCHEME = { Flat: 0, Tiered: 1 } as const satisfies Record<string, PricingSchemeType>;

export const isTiered = ({ pricingScheme }: { pricingScheme?: number | null }): boolean => (pricingScheme ?? PRICING_SCHEME.Flat) === PRICING_SCHEME.Tiered;

export const APPROVAL_STATUS = { Draft: 0, PendingReview: 1, Approved: 2, Rejected: 3 } as const satisfies Record<string, ItemApprovalStatus>;

export type PlanStatus = keyof typeof APPROVAL_STATUS;

const PLAN_STATUSES = Object.keys(APPROVAL_STATUS) as PlanStatus[];

export const toPlanStatus = (value: ItemApprovalStatus | null | undefined): PlanStatus => PLAN_STATUSES.find((status) => APPROVAL_STATUS[status] === value) ?? 'Draft';

export const isIncludedGroup = ({ status }: { status?: string | null }): boolean => (status ?? '').toLowerCase() !== 'optional';

export const isDraftApproval = (value: ItemApprovalStatus | null | undefined): boolean => (value ?? APPROVAL_STATUS.Draft) === APPROVAL_STATUS.Draft;

export const toTierKey = (packageId: string, tierId: string | undefined) => `${packageId}-${tierId}`;

export const toGroupKey = (packageId: string, groupId: string | undefined) => `${packageId}-${groupId}`;

export const toProductKey = (packageId: string, tierId: string | undefined, groupId: string | undefined, productId: string | undefined) =>
    `${packageId}-${tierId}-${groupId}-${productId}`;

export const toPlanKey = (packageId: string, tierId: string | undefined, planIdentifier: string, productId: string | undefined) =>
    `${packageId}-${tierId}-${planIdentifier}-${productId}`;

export const toPricePointKey = (packageId: string, tierId: string | undefined, groupId: string | undefined, productId: string | undefined, planIdentifier: string) =>
    `${packageId}-${tierId}-${groupId}-${productId}-${planIdentifier}`;

export interface PricePointRef {
    packageId: string;
    tier: TierDto;
    group: ProductGroupDto;
    product: ProductDto;
    planIdentifier: string;
    pricePoint: PricePointDto;
    productKey: string;
    planKey: string;
    pricePointKey: string;
}

export const forEachPricePoint = (pkg: PackageSimulationDto, packageId: string, visit: (ref: PricePointRef) => void): void => {
    (pkg.tiers ?? []).forEach((tier) => {
        (tier.productGroups ?? []).forEach((group) => {
            (group.products ?? []).forEach((product) => {
                const productKey = toProductKey(packageId, tier.tierId, group.groupId, product.productId);
                Object.entries(product.prices ?? {}).forEach(([planIdentifier, pricePoint]) => {
                    visit({
                        packageId,
                        tier,
                        group,
                        product,
                        planIdentifier,
                        pricePoint,
                        productKey,
                        planKey: toPlanKey(packageId, tier.tierId, planIdentifier, product.productId),
                        pricePointKey: toPricePointKey(packageId, tier.tierId, group.groupId, product.productId, planIdentifier),
                    });
                });
            });
        });
    });
};

export interface PlanChange {
    hasWorksheetValue: boolean;
    currentPrice: number;
    newPrice: number;
    adjustmentPercentage: number;
    hasChange: boolean;
    isKept: boolean;
    tieredPrices: TieredPricePointDto[];
}

const resolveTier = (tp: TieredPricePointDto, edit: TierAdjustmentValue | undefined): TieredPricePointDto => {
    const isKept = edit !== undefined ? edit.isKept === true : tp.isKept === true;
    if (isKept) return { ...tp, adjustmentPercentage: 0, newUnitPrice: tp.currentUnitPrice, isKept: true };
    if (edit === undefined) return tp;
    return { ...tp, adjustmentPercentage: edit.percentage, newUnitPrice: edit.newUnitPrice, isKept: false };
};

export const hasSavedKept = (pricePoint: PricePointDto): boolean => pricePoint.isKept === true || (pricePoint.tieredPrices ?? []).some((tp) => tp.isKept === true);

export const resolvePlanChange = (pricePoint: PricePointDto, adjustment: AdjustmentValue | undefined): PlanChange => {
    if (isTiered(pricePoint)) {
        const tierAdjustments = adjustment?.tiers;
        const tieredPrices = (pricePoint.tieredPrices ?? []).map((tp) => resolveTier(tp, tierAdjustments?.[tp.index ?? 0]));
        const currentPrice = sumRounded(tieredPrices.map((tp) => tp.currentUnitPrice ?? 0));
        const newPrice = sumRounded(tieredPrices.map((tp) => tp.newUnitPrice ?? tp.currentUnitPrice ?? 0));
        return {
            hasWorksheetValue: Object.keys(tierAdjustments ?? {}).length > 0,
            currentPrice,
            newPrice,
            adjustmentPercentage: percentChange(currentPrice, newPrice),
            hasChange: tieredPrices.some((tp) => (tp.newUnitPrice ?? tp.currentUnitPrice ?? 0) !== (tp.currentUnitPrice ?? 0)),
            isKept: tieredPrices.some((tp) => tp.isKept === true),
            tieredPrices,
        };
    }

    const isKept = adjustment !== undefined ? adjustment.isKept === true : pricePoint.isKept === true;

    const currentPrice = pricePoint.currentPrice ?? 0;
    const newPrice = isKept ? currentPrice : adjustment !== undefined ? adjustment.newPrice : (pricePoint.newPrice ?? currentPrice);
    const adjustmentPercentage = isKept ? 0 : adjustment !== undefined ? adjustment.percentage : (pricePoint.adjustmentPercentage ?? 0);
    return {
        hasWorksheetValue: adjustment !== undefined,
        currentPrice,
        newPrice,
        adjustmentPercentage,
        hasChange: adjustmentPercentage !== 0 || newPrice !== currentPrice,
        isKept,
        tieredPrices: [],
    };
};

export const isListedForApproval = (pricePoint: PricePointDto, change: PlanChange): boolean =>
    isDraftApproval(pricePoint.approvalStatus) && (change.isKept || (change.hasChange && (pricePoint.isModified === true || change.hasWorksheetValue)));

export const isUnsavedModification = (pricePoint: PricePointDto, change: PlanChange): boolean =>
    isDraftApproval(pricePoint.approvalStatus) && change.hasWorksheetValue && (change.hasChange || change.isKept || pricePoint.isModified === true || hasSavedKept(pricePoint));
